import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import SectionHeader from "./SectionHeader.jsx";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";

const ArrowButton = ({ onClick, label, children }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="grid h-10 w-10 place-items-center rounded-full border border-[var(--border)] bg-white text-[var(--ink)] shadow-soft-sm transition hover:border-[var(--gold)] hover:text-[var(--gold)]"
  >
    {children}
  </button>
);

const buildPackageDestinationLink = (item) => {
  const destination = item.slug || item.destination_slug || item.title || item.name || "";
  const query = destination ? `?destination=${encodeURIComponent(destination)}` : "";
  return `/packages/list${query}`;
};

const normalizeItem = (item, index) => {
  const rawLink = item.link || item.cta_link || item.ctaLink || "";
  const link = !rawLink || rawLink === "/listings" || rawLink.startsWith("/listings?")
    ? buildPackageDestinationLink(item)
    : rawLink;
  return {
    id: item.id || item.slug || `destination-${index}`,
    title: item.title || item.name || "Destination",
    subtitle: item.subtitle || item.tagline || "",
    image: resolveMediaUrl(item.image || item.imageUrl || item.image_url || ""),
    link,
  };
};

export default function PopularDestinations({ items = [] }) {
  const sliderRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const slides = useMemo(
    () =>
      (Array.isArray(items) ? items : [])
        .map((item, index) => normalizeItem(item, index))
        .filter((item) => item.image),
    [items]
  );

  useEffect(() => {
    const updateViewport = () => {
      setIsMobile(window.matchMedia("(max-width: 640px)").matches);
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  useEffect(() => {
    setActiveIndex((current) => (slides.length ? Math.min(current, slides.length - 1) : 0));
  }, [slides.length]);

  if (!slides.length) return null;

  const goPrevious = () => {
    if (isMobile) {
      setActiveIndex((current) => (current - 1 + slides.length) % slides.length);
      return;
    }
    sliderRef.current?.slickPrev?.();
  };

  const goNext = () => {
    if (isMobile) {
      setActiveIndex((current) => (current + 1) % slides.length);
      return;
    }
    sliderRef.current?.slickNext?.();
  };

  const settings = {
    dots: false,
    arrows: false,
    infinite: slides.length > 3,
    slidesToShow: Math.min(3, slides.length),
    slidesToScroll: 1,
    speed: 600,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: Math.min(2, slides.length) } },
      { breakpoint: 640, settings: { slidesToShow: 1 } },
    ],
  };

  return (
    <section className="bg-[var(--surface)] py-10 md:py-12" id="destinations">
      <div className="landing-container">
        <SectionHeader
          eyebrow="Popular Destinations"
          title="Popular Destinations"
          action={
            slides.length > 1 ? (
              <div className="flex items-center gap-2">
                <ArrowButton
                  label="Previous destination"
                  onClick={goPrevious}
                >
                  <span className="text-lg leading-none">{"\u2039"}</span>
                </ArrowButton>
                <ArrowButton
                  label="Next destination"
                  onClick={goNext}
                >
                  <span className="text-lg leading-none">{"\u203A"}</span>
                </ArrowButton>
              </div>
            ) : null
          }
        />

        <div className="mt-4 rounded-[26px] border border-[var(--border)] bg-white p-4 shadow-soft-lg md:p-5">
          {isMobile ? (
            <div className="px-0">
              {(() => {
                const item = slides[activeIndex] || slides[0];
                return (
                  <Link
                    to={item.link}
                    className="group relative flex h-[280px] w-full items-end overflow-hidden rounded-[22px] shadow-soft-md"
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(7,20,36,0.72)] via-[rgba(7,20,36,0.18)] to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="text-[2rem] font-homevilas leading-none">{item.title}</h3>
                      {item.subtitle ? (
                        <p className="mt-1 text-[12px] text-white/80">{item.subtitle}</p>
                      ) : null}
                    </div>
                  </Link>
                );
              })()}
              {slides.length > 1 ? (
                <div className="mt-4 flex items-center justify-center gap-2">
                  {slides.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      aria-label={`Show ${item.title}`}
                      onClick={() => setActiveIndex(index)}
                      className={`h-2.5 rounded-full transition-all ${
                        index === activeIndex ? "w-6 bg-[var(--ink)]" : "w-2.5 bg-[var(--border)]"
                      }`}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <Slider ref={sliderRef} {...settings}>
              {slides.map((item) => (
                <div key={item.id} className="px-2">
                  <Link
                    to={item.link}
                    className="group relative flex h-[220px] items-end overflow-hidden rounded-[20px] shadow-soft-md sm:h-[228px]"
                  >
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(7,20,36,0.72)] via-[rgba(7,20,36,0.18)] to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="text-[1.85rem] font-homevilas leading-none">{item.title}</h3>
                      {item.subtitle ? (
                        <p className="text-[12px] text-white/80">{item.subtitle}</p>
                      ) : null}
                    </div>
                  </Link>
                </div>
              ))}
            </Slider>
          )}
        </div>
      </div>
    </section>
  );
}
